import React from 'react';
import { FileCheck, AlertCircle, Calendar, CheckCircle, Plus, Eye } from 'lucide-react';
// FT-356: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

// SMS Reviews Content Component
// FT-356: register onBack (if any) as the shell-level back handler.
export const SMSReviewsContent = ({ reviews, onAddReview, onEditReview, onBack }) => {
  useSetBackHandler(onBack);
  const getNextReviewStatus = () => {
    if (reviews.length === 0) return { status: 'due', message: 'No reviews recorded' };
    
    const lastReview = reviews.sort((a, b) => new Date(b.reviewDate) - new Date(a.reviewDate))[0];
    const nextDue = new Date(lastReview.nextDue);
    const now = new Date();
    const daysUntilDue = Math.floor((nextDue - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDue < 0) {
      return { status: 'overdue', message: `Overdue by ${Math.abs(daysUntilDue)} days` };
    } else if (daysUntilDue < 90) {
      return { status: 'due-soon', message: `Due in ${daysUntilDue} days` };
    } else {
      return { status: 'compliant', message: `Next review in ${daysUntilDue} days` };
    }
  };

  const reviewStatus = getNextReviewStatus();

  return (
    <div className="space-y-6">
      {/* FT-356: Back navigation moved to shell-level MobileBottomBar. */}
      {/* Status Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center mb-4">
              <FileCheck className="h-6 w-6 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">SMS Reviews (3-yearly)</h3>
            </div>
            <p className="text-gray-600 mb-4">Safety Management System reviews as required by regulations</p>
            
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              reviewStatus.status === 'overdue' ? 'bg-red-100 text-red-800' :
              reviewStatus.status === 'due-soon' ? 'bg-yellow-100 text-yellow-800' :
              'bg-green-100 text-green-800'
            }`}>
              {reviewStatus.status === 'overdue' ? <AlertCircle className="h-4 w-4 mr-1" /> :
               reviewStatus.status === 'due-soon' ? <Calendar className="h-4 w-4 mr-1" /> :
               <CheckCircle className="h-4 w-4 mr-1" />}
              {reviewStatus.message}
            </div>
          </div>
          
          <button
            onClick={onAddReview}
            className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Record Review
          </button>
        </div>
      </div>

      {/* Review History */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Review History</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {reviews.length > 0 ? (
            reviews.sort((a, b) => new Date(b.reviewDate) - new Date(a.reviewDate)).map(review => (
              <div key={review.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">
                        {review.reviewType === 'full' ? 'Full Review' : 
                         review.reviewType === 'partial' ? 'Partial Review' : 'Audit'}
                      </span>
                      <span className="ml-2 text-sm text-gray-500">
                        {new Date(review.reviewDate).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">Reviewer: {review.reviewer}</p>
                    {review.findings && review.findings.length > 0 && (
                      <p className="mt-1 text-sm text-gray-600">
                        {review.findings.length} findings recorded
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => onEditReview(review)}
                    className="text-cyan-600 hover:text-cyan-900"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-12 text-center text-gray-500">
              No reviews recorded yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};